import React, { useState } from 'react';
import { X, Send, Reply, MoreHorizontal, Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { Comment } from '@shared/types';
import { useAuth } from '@shared/contexts/AuthContext';
import { Button } from '@shared/components/ui/button';
import { Textarea } from '@shared/components/ui/textarea';
import { cn } from '@shared/lib/utils';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

interface CommentsPanelProps {
  noteId: string;
  onClose: () => void;
}

export default function CommentsPanel({ noteId, onClose }: CommentsPanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['comments', noteId],
    queryFn: () => api.getComments(noteId),
    enabled: !!noteId,
  });

  const addMutation = useMutation({
    mutationFn: (data: { content: string; parent_comment_id?: string }) =>
      api.addComment(noteId, data.content, data.parent_comment_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', noteId] });
      setNewComment('');
      setReplyTo(null);
      toast.success('Comment added');
    },
    onError: () => toast.error('Failed to add comment'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      api.updateComment(noteId, commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', noteId] });
      setEditingId(null);
      toast.success('Comment updated');
    },
    onError: () => toast.error('Failed to update comment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(noteId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', noteId] });
      toast.success('Comment deleted');
    },
    onError: () => toast.error('Failed to delete comment'),
  });

  const handleSubmit = () => {
    const content = newComment.trim();
    if (!content) return;
    addMutation.mutate({
      content,
      parent_comment_id: replyTo?.comment_id,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
  };

  // Build threaded structure: top-level comments + their replies
  const topLevel = comments.filter(c => !c.parent_comment_id);
  const replies = (parentId: string) => comments.filter(c => c.parent_comment_id === parentId);

  return (
    <aside className="w-72 h-full flex flex-col border-l border-border bg-background shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-sm">Comments</h3>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
        ) : topLevel.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No comments yet</p>
        ) : (
          topLevel.map(comment => (
            <div key={comment.comment_id}>
              <CommentItem
                comment={comment}
                currentUserId={user?.user_id}
                editingId={editingId}
                editContent={editContent}
                onStartEdit={(c) => { setEditingId(c.comment_id); setEditContent(c.content); }}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={(id) => updateMutation.mutate({ commentId: id, content: editContent })}
                onEditContentChange={setEditContent}
                onDelete={(id) => deleteMutation.mutate(id)}
                onReply={(c) => { setReplyTo(c); }}
              />
              {/* Replies */}
              <div className="ml-6 mt-1 space-y-1">
                {replies(comment.comment_id).map(reply => (
                  <CommentItem
                    key={reply.comment_id}
                    comment={reply}
                    currentUserId={user?.user_id}
                    editingId={editingId}
                    editContent={editContent}
                    onStartEdit={(c) => { setEditingId(c.comment_id); setEditContent(c.content); }}
                    onCancelEdit={() => setEditingId(null)}
                    onSaveEdit={(id) => updateMutation.mutate({ commentId: id, content: editContent })}
                    onEditContentChange={setEditContent}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    onReply={(c) => setReplyTo(c)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-border space-y-2">
        {replyTo && (
          <div className="flex items-center justify-between text-xs bg-accent rounded px-2 py-1">
            <span className="text-muted-foreground truncate">
              Replying to <span className="font-medium text-foreground">{replyTo.user?.name || 'user'}</span>
            </span>
            <button onClick={() => setReplyTo(null)} className="ml-2 shrink-0">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment… (⌘↵ to send)"
            className="min-h-[60px] text-sm resize-none"
          />
          <Button
            size="icon"
            className="shrink-0 self-end"
            onClick={handleSubmit}
            disabled={!newComment.trim() || addMutation.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  );
}

function CommentItem({
  comment,
  currentUserId,
  editingId,
  editContent,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditContentChange,
  onDelete,
  onReply,
}: {
  comment: Comment;
  currentUserId?: string;
  editingId: string | null;
  editContent: string;
  onStartEdit: (c: Comment) => void;
  onCancelEdit: () => void;
  onSaveEdit: (id: string) => void;
  onEditContentChange: (v: string) => void;
  onDelete: (id: string) => void;
  onReply: (c: Comment) => void;
}) {
  const isOwn = comment.user_id === currentUserId;
  const isEditing = editingId === comment.comment_id;

  return (
    <div className="group rounded-lg p-2 hover:bg-accent/40 transition-colors">
      <div className="flex items-start gap-2">
        {comment.user?.avatar ? (
          <img src={comment.user.avatar} alt="" className="h-6 w-6 rounded-full shrink-0 mt-0.5" />
        ) : (
          <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5 text-xs font-medium">
            {(comment.user?.name || '?')[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-xs font-medium">{comment.user?.name || 'Unknown'}</span>
            <span className="text-xs text-muted-foreground/60">
              {new Date(comment.created_at).toLocaleDateString()}
            </span>
          </div>
          {isEditing ? (
            <div className="space-y-1">
              <Textarea
                value={editContent}
                onChange={e => onEditContentChange(e.target.value)}
                className="min-h-[60px] text-sm resize-none"
                autoFocus
              />
              <div className="flex gap-1">
                <Button size="sm" className="h-6 text-xs" onClick={() => onSaveEdit(comment.comment_id)}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={onCancelEdit}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words">{comment.content}</p>
          )}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            className="p-0.5 rounded hover:bg-accent text-muted-foreground"
            title="Reply"
            onClick={() => onReply(comment)}
          >
            <Reply className="h-3.5 w-3.5" />
          </button>
          {isOwn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-0.5 rounded hover:bg-accent text-muted-foreground">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onStartEdit(comment)}>Edit</DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(comment.comment_id)}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
